import React, { useContext, useState, useEffect, useRef } from "react";
import { useDropzone } from 'react-dropzone';
import { toastrMsg } from './utils/alert';
import ChatGlobalContext from "./context/ChatGlobalContext";
import ChatHeader from "./components/ChatHeader";
import MessageList from "./components/MessageList";
import ChatInput from "./components/ChatInput";
import { chatLocalStorage, closeConversation, createConversation, createLead, didAgentPost, createCase, scrollToBottom, sendMessage, createEmailCase } from "./services/chatService";
import loadingIcon from './assets/images/loader-transparent.gif';
import notificationSound from "./assets/audio/notification.wav";
import ChatContent from "./components/styled/ChatContent";
import { initialTwilioChatState, userTypeLabels } from "./utils/chats";
import { Lightbox } from "react-modal-image";
import moment from "moment";
import linkifyHtml from "linkify-html";
import ChatNotificationBar from "./components/ChatNotificationBar";
import { isStagingOrDevelopment } from './utils/helperFn';
import { useTime } from "react-timer-hook";

const Chat = ({...props}) => {
    const gContext: any = useContext(ChatGlobalContext);

    let timer = useRef<number>(0);
    let [countdownInterval, setCountdownInterval] = useState<any>(null);

    const titleRef = useRef<string>("");
    const intervalRef = useRef<any | null>(null);

    const { seconds: londonSec } = useTime({ interval: 1 });
    const [appMessages, setAppMessages] = useState<any>(null);
    const [chatActive, setChatActive] = useState<boolean>(true);
    const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
    const [leadId, setLeadId] = useState<string>('');
    
    useEffect(() => {
      gContext.goSetTwilioChat({
        ...gContext?.goGetTwilioChat(),
        outage: false,
        outageMessage: null,
        outageTitle: null,
        salesMessage: null,
        fullScreen: props?.fullscreen || false
      });  
    }, []);

    useEffect(() => {
      // Get Settings from Strapi
      setAppMessages(gContext.appMessages);
      // Chech if current time isn't in time range
      // setChatActive(!isTimeInRange());
      gContext?.goSetTwilioChat({
        ...gContext?.goGetTwilioChat(), 
        // chatActive: !isTimeInRange()
      });
    }, [gContext.appMessages]);
    
    useEffect(() => {
      // Live check if chat should be active
      // setChatActive(!isTimeInRange());
    }, [londonSec]);
  
    useEffect(() => {
      // If we are on the second step - Login form
      !chatActive && !gContext?.goGetTwilioChat()?.chatCaseId && resetChat();
    }, [chatActive]);
  
    const nowInLondon = new Date().toLocaleString("en-GB", {
      timeZone: "Europe/London",
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  
    const timeToSeconds = (timeStr) => {
      if(timeStr) {
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
      }
    }
  
    const isTimeInRange = () => {
      if(gContext.appMessages?.chat_off_mode_toggle && nowInLondon && gContext.appMessages?.chat_off_mode_start && gContext.appMessages?.chat_off_mode_end) {
        const current = timeToSeconds(nowInLondon);
        const start = timeToSeconds(gContext.appMessages?.chat_off_mode_start);
        const end = timeToSeconds(gContext.appMessages?.chat_off_mode_end);
        if (start < end) {
          // Normal range (e.g., 08:00 to 20:00)
          return current >= start && current < end;
        } else {
          // Overnight range (e.g., 20:00 to 08:00)
          return current >= start || current < end;
        }
      } else {
        return false;
      }
    }

    useEffect(() => {
      if(typeof window !== 'undefined' && typeof document !== "undefined" && gContext?.goGetTwilioChat()?.chatUnreaded) {
        localStorage.setItem("title", document.title);
        let sound = new Audio(notificationSound);
        sound.setAttribute('crossorigin', 'anonymous'); 
        sound.play().catch((error) => {
          console.log("User interaction required before playing sound");
        });
        document.title = "::: You have new message";
      } else {
        if(localStorage.getItem("title")) document.title = localStorage.getItem("title") || "";
      }
    }, [gContext?.goGetTwilioChat()?.chatUnreaded]);


    useEffect(() => {
      const fetchData = async () => {
        try {
          if(typeof window !== 'undefined' && typeof document !== "undefined"){
            if(gContext?.goGetTwilioChat()?.openChat){
              if(window.innerWidth < 577) document.body.style.overflow = "hidden";
              intervalRef.current && clearInterval(intervalRef.current);
              // if(localStorage.getItem("title")) document.title = localStorage.getItem("title") || "";
              document.getElementById('inputMessage')?.focus();
              setTimeout(() => localStorage.removeItem("title"), 1000);

              gContext?.goSetTwilioChat({
                ...gContext?.goGetTwilioChat(), 
                chatUnreaded: false
              });

            } else {
              document.body.style.overflow = "";
              gContext?.goSetTwilioChat({
                ...gContext?.goGetTwilioChat(), 
                chatUnreaded: false,
                chatClosing: false
              });
            }
          }
          scrollToBottom();
        } catch (error) {
          console.log("Error fetching data:", error);
        }
      };    
      fetchData();
    }, [gContext?.goGetTwilioChat()?.openChat]);
    
    useEffect(() => {
        if(!countdownInterval && didAgentPost(gContext?.goGetTwilioChat()?.messages)){
            setCountdownInterval(setInterval(() => {
            if(gContext?.goGetTwilioChat()?.lastTimeActive !== null){
                const elapsedSeconds = timer.current + 1;
                localStorage.setItem("timer", elapsedSeconds.toString());
                if(elapsedSeconds === parseInt(process.env.GATSBY_TWILIO_TIME1 || '30000000000')) {    // After: 300s = 5min
                    gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), messages: [{
                        senderType: 'AGENT',
                        createdAt: moment(new Date()).format(),
                        from: 'YouFibre',
                        message: "Could you please confirm if you're still waiting on this chat? If so, kindly send a reply. If we don’t hear from you, the chat will automatically close. Thank you.",
                        form: null,
                        files: [],
                        messageId: 321,
                        fromId: `${Date.now()}-0000-0000-0000-000000000000`
                    }, ...gContext?.goGetTwilioChat()?.messages]});
                    timer.current = elapsedSeconds;
                } else if(elapsedSeconds > parseInt(process.env.GATSBY_TWILIO_TIME2 || '30000000060')){ // After: 360s = 6min
                    closeConversation("SYSTEM");
                    gContext?.goSetTwilioChat({
                      ...gContext?.goGetTwilioChat(), 
                      ...initialTwilioChatState, 
                      openChat: gContext?.goGetTwilioChat()?.openChat, 
                      chatClosing: true, 
                      chatClosingType: "SYSTEM",
                      fullScreen: props?.fullscreen || false,
                      side: gContext?.goGetTwilioChat()?.side
                    });
                    clearInterval(countdownInterval);
                    localStorage.removeItem("timer");
                    setCountdownInterval(null);
                    timer.current = 0;
                } else {
                    timer.current = elapsedSeconds;
                }
            } else {
                clearInterval(countdownInterval);
                localStorage.removeItem("timer");
                setCountdownInterval(null);
                countdownInterval = null;
                timer.current = 0;
            }
            return true;
            }, 1000)); 
        } else {
            if(!didAgentPost(gContext?.goGetTwilioChat()?.messages)){
                clearInterval(countdownInterval);
                setCountdownInterval(null);
                countdownInterval = null;
            }
            timer.current = 0;
        }
      }, [gContext?.goGetTwilioChat()?.lastTimeActive]);
    
    // Upload files - Configuration
    const fileCount = 5;
    const fileSize = 10; // MB
    const fileExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'gif', 'txt', 'mp4', 'mp3'];

    // Upload files - for input form or drag&drop over chat
    const {getRootProps, getInputProps, open} = useDropzone({
        noClick: true,
        noKeyboard: true,
        onDrop: async (acceptedFiles:File[]) => {
            gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), uploadArea: false});
            if(gContext?.goGetTwilioChat()?.chatCaseId){
                acceptedFiles = acceptedFiles.filter(item => item.size < (fileSize*1048576) ? item : toastrMsg(`Maximum file size is ${fileSize}MB`, "error"));
                acceptedFiles = acceptedFiles.filter(item => fileExtensions.some(ext => item.name.endsWith(ext)) ? item : toastrMsg(`Unsupported File extension`, "error"));
                acceptedFiles = acceptedFiles.filter(item => Object.keys(attachmentFiles?.filter(ext => ext.name === item.name)).length === 0);
                if(Object.keys(attachmentFiles).length + Object.keys(acceptedFiles).length < fileCount+1){      
                    setAttachmentFiles([...attachmentFiles || [], ...acceptedFiles]);       
                } else {
                    toastrMsg(`Maximum ${fileCount} files`, "warning");
                    gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), uploadArea: false});
                }
            }
        },
    });

    const resetChat = () => {
      setLeadId('');
      gContext.goSetTwilioChat({
        ...initialTwilioChatState,
        openChat: false
      });
    };

    const resetChatOutage = () => {
      gContext.goSetTwilioChat({
        ...gContext?.goGetTwilioChat(),
        outage: false,
        outageMessage: null,
        outageTitle: null,
        contactSales: false,
        salesMessage: null,
        openChat: true
      });  
    };

    const getFormData = () => {   
      const data = gContext?.goGetTwilioChat();
      return {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        address: data.address,
        phone: data.phone,
        postalCode: data.postalCode,
        SPRN: data.SPRN,
        UDPRN: data.UDPRN,
        UPRN: data.UPRN,
        UMPRN: data.UMPRN,
      };
    };

    const setConversationData = (conversationData, chatForm) => {
      let tmpMessage:any = {};
      if (conversationData?.statusCode === 409 || conversationData?.status === 409) {
        tmpMessage = conversationData?.data?.body ? {
            outage: true,
            outageMessage: conversationData?.data?.body,
            outageTitle: conversationData?.data?.header,
            allowCreateChat: conversationData?.data?.allowCreateChat,
            contactSales: false,
            salesMessage: null,
          } : {
            outage: true,
            outageMessage: conversationData?.message,
            outageTitle: "We're offline right now",
            allowCreateChat: false,
            contactSales: false,
            salesMessage: null,
          };
      } else if ((conversationData?.statusCode === 200 || conversationData?.statusCode === 201 || conversationData?.status === 200 || conversationData?.status === 201) && conversationData?.data) {
        tmpMessage = {
          logged: true,
          chatUserId: conversationData?.data?.participantId,
          chatUsername: chatForm.firstName,
          chatConversationId: conversationData?.data?.conversationId,
          token: conversationData?.data?.caseId,
          chatCaseId: conversationData?.data?.caseId,
          messages: [{            
            senderType: 'EVENT',
            createdAt: moment(new Date()).format(),
            from: 'YouFibre',
            message: "Waiting for the agent to join the chat.",
            form: null,
            files: [],
            messageId: `${Date.now()}-0000-0000-0000-000000000000`,
            fromId: 'YouFibre'
          }],
          visibilityChange: true
        };
      } else {
        tmpMessage = {
          outage: true,
          outageTitle: "We're offline right now",
          outageMessage: "Please leave us a message, and a member of our Sales Team will get back to you when they're back online.<br /><br />Chat open hours:<br />Monday to Friday 8am to 8pm.<br />Saturday 10am to 6pm.",
          allowCreateChat: false,
          contactSales: true,
          salesMessage: null,
        };
      }
      gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), ...tmpMessage, chatLoading: false});
    };

    const sendInitialMessage = () => {
      sendMessage(`
        <b>${userTypeLabels[gContext?.goGetTwilioChat()?.chatUserType]}</b><br />
        <b>Full name</b>: ${getFormData().firstName} ${getFormData().lastName}
        <b>Email</b>: ${getFormData().email}
        <b>Phone</b>: ${getFormData().phone}
        <b>Address</b>: ${getFormData().address}
      `); 
    };

    const sendEmailToSales = async () => {
      if(gContext?.goGetTwilioChat()?.salesMessage){
        const payloadEmailCase = {
          channel: 'EMAIL',
          source: 'TWILIO',
          DpaCustomerName: getFormData().firstName + " " + getFormData().lastName,
          DpaCustomerAddress: getFormData().address,
          DpaCustomerEmail: getFormData().email,
          DpaCustomerPhone: getFormData().phone,
          message: gContext?.goGetTwilioChat()?.salesMessage
        };
        try {
          const newCase:any = await createEmailCase(payloadEmailCase); 
          if(newCase){
                console.log("Message sent to sales: ");  
          } else {
            console.log("Failed to create email case");            
          }
          gContext?.goSetTwilioChat({
            ...initialTwilioChatState,   
            question: true,
            questionBtn2Action: 'close_question',
            questionBtn2Title: 'Close',
            questionTitle: "Thank you for your interest.",
            questionMessage: "Sales team will get back in touch with you when they are online.",
            chatLoading: false,
            openChat: true
          }); 
        } catch (error:any) {
          console.log("Error: ", error);
        }
      }
    };

    const errorFetchingMessage = (err:any = '') => {
      gContext?.goSetTwilioChat({
        ...gContext?.goGetTwilioChat(),   
        question: true,
        questionBtn2Action: 'close_question',
        questionBtn2Title: 'Close',
        questionTitle: 'ERROR',
        questionMessage: 'An error occured while processing your request.',
        chatLoading: false,
      });   
      console.log("Error: ", err);      
    };

    const hideQuestion = () => {
      gContext?.goSetTwilioChat({
        ...gContext?.goGetTwilioChat(),   
        question: false,
        questionBtn1Action: null,
        questionBtn1Title: null,
        questionBtn2Action: null,
        questionBtn2Title: null,
        questionTitle: null,
        questionMessage: null,
        chatLoading: true,
      });
    };
        
    const buttonAction = async (action) => {
      if(gContext?.goGetTwilioChat()?.chatActive || gContext?.goGetTwilioChat()?.chatCaseId){
        switch(action){
          case 'close_question': 
            gContext?.goResetTwilioChat();
            break;
          case 'no_consent': 
            hideQuestion();
            try {
              const newLeadNo = await createLead({...getFormData(), marketingConsent: false, MarketingSMS: false});
              if(newLeadNo){
                setLeadId(newLeadNo.data[0].id);
                if(gContext?.goGetTwilioChat().SalesStatus?.toUpperCase() ==="ORDER") {
                  try {
                    const newConversationNo = await createConversation(getFormData());
                    if(newConversationNo && newConversationNo?.data?.conversationId){
                      setConversationData(newConversationNo, getFormData());
                      sendInitialMessage();
                      // setTimeout(() => sendInitialMessage(), 700);
                    } else {
                      setConversationData(newConversationNo, getFormData());
                    }
                  } catch (error:any) {
                    errorFetchingMessage(error);  
                  }
                } else {
                  gContext?.goSetTwilioChat({
                    ...gContext?.goGetTwilioChat(),   
                    question: true,
                    questionBtn2Action: 'close_question',
                    questionBtn2Title: 'Close',
                    questionTitle: "Thank you for reaching out.",
                    questionMessage: "It shouldn’t be too long - we’re adding thousands of homes to our network every week.",
                    chatLoading: false,
                  }); 
                }
              }
            } catch (error:any) {
              errorFetchingMessage(error);  
            }
            break;
          case 'yes_consent': 
            hideQuestion();
            try {
              const newLeadYes = await createLead({...getFormData(), marketingConsent: true, MarketingSMS: true});
              if(newLeadYes){
                setLeadId(newLeadYes.data[0].id);
                if(gContext?.goGetTwilioChat().SalesStatus?.toUpperCase() === "ORDER") {
                  try {
                    const newConversationYes = await createConversation(getFormData());
                    if(newConversationYes && newConversationYes?.data?.conversationId){
                      setConversationData(newConversationYes, getFormData());
                      sendInitialMessage();
                      // setTimeout(() => sendInitialMessage(), 700);
                    } else {
                      setConversationData(newConversationYes, getFormData());
                    }
                  } catch (error:any) {
                    errorFetchingMessage(error);  
                  }
                } else {
                  gContext?.goSetTwilioChat({
                    ...gContext?.goGetTwilioChat(),   
                    question: true,
                    questionBtn2Action: 'close_question',
                    questionBtn2Title: 'Close',
                    questionTitle: "Thank you for registering your interest.",
                    questionMessage: "We're not with you yet, but now you're registered you'll hear from us as soon as we can connect you.",
                    chatLoading: false,
                  }); 
                }
              } else {
                errorFetchingMessage();           
              }
            } catch (error:any) {
              errorFetchingMessage(error);  
            }
            break;
          default: break;
        }
      }
    };

    return (
        <ChatContent
            className={`${gContext?.goGetTwilioChat()?.openChat ? 'show' : ''} ${props?.fullscreen ? 'fullscreen':''}`}
            {...getRootProps()}
            onDragOver={() => gContext?.goGetTwilioChat()?.chatCaseId && gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), uploadArea: true})}
            onMouseLeave={() => gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), uploadArea: false})}
        >
            {gContext?.goGetTwilioChat()?.lightboxUrl && <Lightbox
                medium={gContext?.goGetTwilioChat()?.lightboxUrl}
                large={gContext?.goGetTwilioChat()?.lightboxUrl}
                showRotate={true}
                onClose={() => gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), lightboxUrl: null})}
            />} 

            <ChatHeader fullScreen={props?.fullscreen || false} />
            <ChatNotificationBar scroll={false} />
            <MessageList />
            <ChatInput open={open} attachmentFiles={attachmentFiles} setAttachmentFiles={setAttachmentFiles} />
            <input {...getInputProps()} />

            {/* Loading cover for chat */}
            { gContext?.goGetTwilioChat()?.chatLoading && gContext?.goGetTwilioChat()?.openChat && <div className='cover'><img className="make-it-white" src={loadingIcon} width={14} height={14} /></div> }
        
            {/* Confirmation for closing conversation from client's side */}
            { gContext?.goGetTwilioChat()?.confirmClosing && gContext?.goGetTwilioChat()?.openChat && <div className='cover closed'>
            <h4>Close conversation</h4>
            <span>Are you sure you want to end this conversation?</span>  
            <button onClick={() => gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), confirmClosing: false})} className="no-bg">Cancel</button>
            <button onClick={() => { closeConversation("CUSTOMER"); }} className='blue-bg'>Confirm</button>
            </div> }

            {/* Closed conversation from agent's or systems side */}
            { gContext?.goGetTwilioChat()?.chatClosing && gContext?.goGetTwilioChat()?.openChat && <div className='cover closed'>
            <h4>Conversation is closed</h4>
            <span>To keep the conversation going, create a new conversation.</span>  
            <button onClick={() => gContext?.goSetTwilioChat({
              ...gContext?.goGetTwilioChat(), 
              ...initialTwilioChatState, 
              openChat: false, 
              fullScreen: props?.fullscreen || false,
              side: gContext?.goGetTwilioChat()?.side
            })}>Close</button>
            <button onClick={() => gContext?.goResetTwilioChat()} className='blue-bg'>Create new</button>
            </div> }

            {/* Upload files */}
            { gContext?.goGetTwilioChat()?.uploadArea && gContext?.goGetTwilioChat()?.chatCaseId && gContext?.goGetTwilioChat()?.openChat && <div className='cover upload'>
                <h4>Drag and drop your files here</h4>
                <p>Maximum {gContext?.goGetTwilioChat()?.fileCount} files. Limit {gContext?.goGetTwilioChat()?.fileSize}MB per file.</p>
                <p>Supported files: {fileExtensions && fileExtensions.map((item:string, index) => !['mp4','mp3'].some(ext => ext === item) && (index === 0 ? item : `, ${item}`))}</p>
            </div> }

            {/* Implementiraj promene koje je Stevan odradio za rebrand u TwilioChat fajlovima */}
            {/* https://github.com/d19n-llc/youfibre-gatsby/commits/rebrand/src/components/TwilioChat/index.tsx */}

            {/* Network Outage */}
            { gContext?.goGetTwilioChat()?.outage && gContext?.goGetTwilioChat()?.outageMessage && <div className='cover closed'>
              <h4 >{gContext?.goGetTwilioChat()?.outageTitle}</h4>
              <span dangerouslySetInnerHTML={{ __html: linkifyHtml(gContext?.goGetTwilioChat()?.outageMessage, { target: "_blank" })}} />
              { gContext?.goGetTwilioChat()?.contactSales && 
                <textarea rows={4} className="w-100 fs-16 p-1 mt-20" style={{resize: 'none'}} value={gContext?.goGetTwilioChat()?.salesMessage} onChange={(e) => gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), salesMessage: e.target.value})}/>
              }
              <button onClick={() => resetChat() } className='no-bg w-100' >Close Chat</button>
              {!gContext?.goGetTwilioChat()?.contactSales && gContext?.goGetTwilioChat()?.allowCreateChat && <button onClick={() => { resetChatOutage();}} className=" w-100">Still Need Help - Start Chat</button> }
              {gContext?.goGetTwilioChat()?.contactSales && <button onClick={() => sendEmailToSales()} className=" w-100">Send Message</button> }
            </div> }

            {/* Chat out of Service */}
            { !chatActive && gContext?.goGetTwilioChat()?.openChat && !gContext?.goGetTwilioChat()?.chatCaseId && appMessages && !isStagingOrDevelopment() && <div className='cover closed'>
              <h4>Out of service</h4>
              <span dangerouslySetInnerHTML={{ __html: linkifyHtml(appMessages.chat_off_mode_text, { target: "_blank" }).replaceAll(/\r\n|\n/g, "<br />") }} />
              {!props?.fullscreen && <button onClick={() => gContext?.goSetTwilioChat({
              ...gContext?.goGetTwilioChat(), 
              openChat: false
              })} className="no-bg">Close chat</button>}
            </div> }

            {/* Question */}
            { gContext?.goGetTwilioChat()?.question && gContext?.goGetTwilioChat()?.questionMessage && <div className='cover closed'>
              <h4 dangerouslySetInnerHTML={{ __html: gContext?.goGetTwilioChat()?.questionTitle || ""}} />
              <span dangerouslySetInnerHTML={{ __html: linkifyHtml(gContext?.goGetTwilioChat()?.questionMessage, { target: "_blank" })}} />
              { gContext?.goGetTwilioChat()?.questionBtn1Action && <button onClick={() => buttonAction(gContext?.goGetTwilioChat()?.questionBtn1Action) } className='no-bg w-100' >{gContext?.goGetTwilioChat()?.questionBtn1Title}</button>}
              { gContext?.goGetTwilioChat()?.questionBtn2Action && <button onClick={() => buttonAction(gContext?.goGetTwilioChat()?.questionBtn2Action) } className=" w-100">{gContext?.goGetTwilioChat()?.questionBtn2Title}</button>}
            </div> }

        </ChatContent>
    );
};

export default Chat;